import React, { useState, useRef } from 'react';
import { GuidanceFormat } from '../types';

interface ActionInputProps {
  userInput: string;
  setUserInput: React.Dispatch<React.SetStateAction<string>>;
  suggestions: string[];
  isThinking: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onSuggestionClick: (suggestion: string) => void;
  guidanceFormats: { [key: string]: GuidanceFormat };
}

const ActionInput: React.FC<ActionInputProps> = ({
  userInput,
  setUserInput,
  suggestions,
  isThinking,
  onSubmit,
  onSuggestionClick,
  guidanceFormats
}) => {
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  // This state tracks the currently selected format, used when applying formats to text
  const [selectedFormat, setSelectedFormat] = useState<string>('guide');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleFormatSelect = (format: string) => {
    setSelectedFormat(format);
    const prefix = guidanceFormats[format].prefix;
    setUserInput(prev => {
      const trimmedInput = prev.trim();
      // If there's already a prefix, replace it
      const existingPrefix = Object.values(guidanceFormats)
        .map(f => f.prefix)
        .find(p => trimmedInput.startsWith(p));
      
      if (existingPrefix) {
        return prefix + trimmedInput.slice(existingPrefix.length);
      }
      // Otherwise, add the new prefix
      return `${prefix} ${trimmedInput}`.trim();
    });
    setShowFormatDropdown(false);
    
    // Focus the textarea after a short delay to ensure state updates are complete
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 0);
  };

  return (
    <>
      <div className="suggestions">
        <div className={`format-suggestion format-${selectedFormat}`}>
          <button
            className="suggestion-btn"
            onClick={() => setShowFormatDropdown(!showFormatDropdown)}
            data-testid="format-suggestion-btn"
          >
            <i className="lni lni-text-format"></i> Add Format
          </button>
          <div className={`format-dropdown ${showFormatDropdown ? 'visible' : ''}`}>
            {Object.entries(guidanceFormats).map(([key, format]) => (
              <div
                key={key}
                className="format-option"
                onClick={() => handleFormatSelect(key)}
              >
                <i className={`lni lni-${
                  key === 'guide' ? 'pointer' :
                  key === 'whisper' ? 'warning' :
                  key === 'insight' ? 'bulb' :
                  'heart'
                }`}></i>
                <div className="format-label">
                  <span className="format-name">{format.prefix}</span>
                  <span className="format-description"> {format.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        {suggestions && suggestions.map((suggestion, index) => (
          <button
            key={index}
            className="suggestion-btn"
            onClick={() => onSuggestionClick(suggestion)}
            disabled={isThinking}
            data-testid={`suggestion-${index}`}
          >
            {suggestion}
          </button>
        ))}
      </div>

      <form onSubmit={onSubmit} className="input-container">
        <div className="input-wrapper">
          <div className="input-group">
            <textarea
              ref={textareaRef}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Type your guidance here..."
              className="action-input"
              disabled={isThinking}
              data-testid="action-input"
              rows={3}
            />
            <button 
              type="submit"
              className="send-icon-btn"
              disabled={isThinking || !userInput.trim()}
              data-testid="send-button"
              aria-label="Send message"
            >
              <i className="lni lni-arrow-right"></i>
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ActionInput; 